import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Alert,
  Box,
  Button,
  Divider,
  SelectOptionGroups,
  Text,
} from '@factorialco/deprecated-design-system'
import {
  TimePlanningPlannedBreakBreakTypeEnum as BreakType,
  TimePlanningPlannedBreakBreakTypeEnumValues as BreakTypeValues,
} from 'generated/resources/datatable.graphql'
import { BreakConfigurationFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import Select from 'modules/core/components/Select'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { isEnumValue } from 'modules/core/lib/utils/enums'

import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'
import { useGetBreakConfigurations } from 'modules/time_categories_settings/hooks/useGetBreakConfigurations'

import BreaksTooltip from './BreaksTooltip'

type From = 'saved-shifts' | 'custom-shifts' | 'work-schedules' | 'rotating-shifts'

interface Props {
  onClose: () => void
  onAddBreak: (
    breakConfiguration: BreakConfigurationFragment,
    breakType: BreakType,
    isTimeFormat24H: boolean
  ) => void
  hideCreateBreakButton?: boolean
  from: From
}

const AddPlannedBreakModal = ({
  onClose,
  onAddBreak,
  from,
  hideCreateBreakButton = false,
}: Props) => {
  const { loading, breakConfigurations } = useGetBreakConfigurations({ active: true })
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [breakConfigId, setBreakConfigId] = useState<number | null>(null)
  const [breakType, setBreakType] = useState<BreakType>('flexible')
  const { isTimeFormat24H } = useIsTimeFormat24H()

  const breakConfigNames: SelectOptionGroups = breakConfigurations.map((bc) => ({
    label: bc.name,
    value: String(bc.id),
  }))

  breakConfigNames.unshift({
    label: i18n.t('shift_management.saved_shifts.breaks_form.name_none'),
    value: null,
  })

  const breakTypeOptions: SelectOptionGroups = [
    {
      label: i18n.t('shift_management.saved_shifts.breaks_form.scheduling.flexible'),
      value: 'flexible',
    },
    {
      label: i18n.t('shift_management.saved_shifts.breaks_form.scheduling.semi_flexible'),
      value: 'semi_flexible',
    },
    {
      label: i18n.t('shift_management.saved_shifts.breaks_form.scheduling.fixed'),
      value: 'fixed',
    },
  ]

  const calloutMessage = {
    fixed: {
      title: i18n.t('shift_management.breaks.callout.fixed.title'),
      description: i18n.t('shift_management.breaks.callout.fixed.description'),
    },
    semi_flexible: {
      title: i18n.t('shift_management.breaks.callout.semi_flexible.title'),
      description: i18n.t('shift_management.breaks.callout.semi_flexible.description'),
    },
    flexible: {
      title: i18n.t('shift_management.breaks.callout.flexible.title'),
      description: i18n.t('shift_management.breaks.callout.flexible.description'),
    },
  } as const

  const onSubmit = (closePortal: ClosePortalFn) => {
    const breakConfiguration = breakConfigurations.find((bc) => bc.id === breakConfigId)
    if (!breakConfiguration) throw 'Break configuration not found'
    onAddBreak(breakConfiguration, breakType, isTimeFormat24H)
    closePortal()
  }

  const onCreateNewBreak = () => {
    track(trackingEvents.shiftManagement.newBreakStart, { from })
    navigate(navigation.settings.time_categories_settings.fullPath())
  }

  const onSetBreakType = (value: string) => {
    if (!isEnumValue(BreakTypeValues, value)) return

    setBreakType(value)
  }

  return (
    <Modal
      illustration="coffee"
      title={i18n.t('shift_management.saved_shifts.breaks_form.title')}
      description={i18n.t('shift_management.saved_shifts.breaks_form.description')}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <Button disabled={!breakConfigId} submit>
          {i18n.t('shift_management.saved_shifts.breaks_form.add')}
        </Button>
      }
    >
      {!breakConfigurations.length && !hideCreateBreakButton ? (
        <Box alignItems="center">
          <Button type="secondary" onClick={onCreateNewBreak} iconName="add">
            {i18n.t('shift_management.saved_shifts.breaks_form.create')}
          </Button>
        </Box>
      ) : (
        <Box>
          {loading && <Loading />}
          {!loading && (
            <Box gap="s24">
              <Box gap="s16">
                <Select
                  label={i18n.t('shift_management.saved_shifts.breaks_form.name')}
                  onChange={(value) => setBreakConfigId(Number(value))}
                  options={breakConfigNames}
                />
                {!!breakConfigId && (
                  <>
                    <Box gap="s6">
                      <Box flexDirection="row" gap="s4" alignItems="center">
                        <Text size="200" weight="medium" color="grey1000">
                          {i18n.t('shift_management.saved_shifts.breaks_form.scheduling.title')}
                        </Text>
                        <BreaksTooltip />
                      </Box>
                      <Select
                        onChange={(value) => onSetBreakType(value)}
                        options={breakTypeOptions}
                      />
                      <Callout
                        type="info"
                        title={calloutMessage[breakType].title}
                        description={calloutMessage[breakType].description}
                      />
                    </Box>
                  </>
                )}
              </Box>
              {hideCreateBreakButton ? (
                breakConfigurations.length ? (
                  <Alert>{i18n.t('shift_management.saved_shifts.breaks_form.alert')}</Alert>
                ) : (
                  <Alert>
                    <Text size="300">
                      {i18n.t('shift_management.saved_shifts.breaks_form.alert_no_breaks')}&nbsp;
                      {i18n.t('shift_management.saved_shifts.breaks_form.alert')}
                    </Text>
                  </Alert>
                )
              ) : (
                <>
                  <Box alignItems="center" flexDirection="row" justifyContent="center" gap="s12">
                    <Box minWidth="s140">
                      <Divider />
                    </Box>
                    {i18n.t('common.or')}
                    <Box minWidth="s140">
                      <Divider />
                    </Box>
                  </Box>
                  <Box alignItems="center">
                    <Button type="secondary" onClick={onCreateNewBreak} iconName="add">
                      {i18n.t('shift_management.saved_shifts.breaks_form.create')}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      )}
    </Modal>
  )
}

export default observer(AddPlannedBreakModal)
